import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom"; // React Router is required for query params
import "../css/ChatMobile.css";
import { marked } from "marked";
import hljs from "highlight.js";
import "highlight.js/styles/github.css"; // Use the GitHub theme for better contrast
import mic from "../assets/mic.png";
import send from "../assets/send.png";
import avatar from "../assets/avatar.png";
import X from "../assets/X.png";
import exit from "../assets/exit.png";
import dropdown from "../assets/dropdown.png";
import dropup from "../assets/dropup.png";
import tick from "../assets/tick.png";
import TrendingCarousel from "./trending";

const ChatMobile = () => {
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [isModelToolkitDropdownActive, setIsModelToolkitDropdownActive] = useState(false); // Model/Toolkit dropdown
  const [type, setType] = useState("basic"); // Default to "basic"
  const [searchParams, setSearchParams] = useSearchParams();
  const toolOptions = ['Google', 'Yahoo Finance', 'CoinGecko', 'Arxiv'];
  const [selectedTools, setSelectedTools] = useState(['Google', 'CoinGecko']); // State to track selected tools in agentic mode
  const [selectedModel, setSelectedModel] = useState('Meta-Llama-3.2-3B-Instruct');
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const modelOptions = [
    { name: 'Meta-Llama-3.2-3B-Instruct', description: 'Optimized for edge devices', available: true },
    { name: 'Meta-Llama-3.3-70B-Instruct', description: 'Advanced insights for complex questions', available: true },
    { name: 'Llama-4-Maverick-17B-128E-Instruct', description: 'Great context-awareness at unparalleled speeds', available: true },
    { name: 'Llama-4-Scout-17B-16E-Instruct', description: 'Great context-awareness at unparalleled speeds', available: true },
    { name: 'DeepSeek-V3-0324', description: 'Great context-awareness at unparalleled speeds', available: true },
  ];
  const [chatHistory, setChatHistory] = useState([]); // Chat history state
  const [currentMessage, setCurrentMessage] = useState(""); // Current message state
  const textareaRef = useRef(null); // Create a ref for the textarea
  const dragStartY = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true); // Auto-scroll state

  const chatWindowRef = useRef(null); // Ref for the chat history container
  const lastUserMessageRef = useRef(null); // Ref for the last user message
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null); // Reference for SpeechRecognition
  const isIOS = () => {
    return (
      /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream
    );
  };

  
  const handleVoiceInput = () => {
    // Check if SpeechRecognition is supported
    if (!("webkitSpeechRecognition" in window)) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    // Initialize SpeechRecognition if it doesn't already exist
    if (!recognitionRef.current) {
      const SpeechRecognition = window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";

      // Set up SpeechRecognition events
      recognitionRef.current.onstart = () => {
        setIsRecording(true);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false); // Stop recording when recognition ends
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setCurrentMessage((prev) => `${prev} ${transcript}`); // Append recognized text to input
      };

      // Handle errors and auto-stop
      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        recognitionRef.current.stop();
      };
    }

    // Toggle recognition start/stop based on `isRecording`
    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
  };
  

    // Scroll Detection Logic
    const handleScroll = () => {
      const chatWindow = chatWindowRef.current;
      if (!chatWindow) return;
  
      // Check if the user is near the bottom
      const isAtBottom =
        chatWindow.scrollTop + chatWindow.clientHeight >= chatWindow.scrollHeight - 5; // Allow slight margin
      setIsAutoScrolling(isAtBottom);
    };


// Auto-scroll when new messages are added
useEffect(() => {
  if (isAutoScrolling && lastUserMessageRef.current) {
    const chatWindow = chatWindowRef.current;

    const offset = chatWindow.clientHeight * 0.07; // 10% of container height
    const targetScrollPosition =
      lastUserMessageRef.current.offsetTop - offset;

    // Smoothly scroll to the calculated position
    chatWindow.scrollTo({
      top: targetScrollPosition,
      behavior: "smooth",
    });
  }
}, [chatHistory]);


useEffect(() => {
  const adjustViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  window.addEventListener("resize", adjustViewportHeight);
  adjustViewportHeight(); // Initial call

  return () => {
    window.removeEventListener("resize", adjustViewportHeight);
  };
}, []);


    // Attach scroll event listener
    useEffect(() => {
      const chatWindow = chatWindowRef.current;
      if (chatWindow) {
        chatWindow.addEventListener("scroll", handleScroll);
      }
  
      return () => {
        if (chatWindow) {
          chatWindow.removeEventListener("scroll", handleScroll);
        }
      };
    }, []);
    
    // Prevent scroll propagation to the parent page or iframe
    useEffect(() => {
      const chatWindow = chatWindowRef.current;
      if (chatWindow) {
        const preventScrollPropagation = (e) => {
          const { scrollTop, scrollHeight, clientHeight } = chatWindow;
          if (
            (scrollTop === 0 && e.deltaY < 0) || // At the top and trying to scroll up
            (scrollTop + clientHeight === scrollHeight && e.deltaY > 0) // At the bottom and trying to scroll down
          ) {
            e.preventDefault(); // Prevent scroll propagation
          }
        };
    
        chatWindow.addEventListener("wheel", preventScrollPropagation);
    
        return () => {
          chatWindow.removeEventListener("wheel", preventScrollPropagation);
        };
      }
    }, []);


  const renderFormattedMessage = (message) => {
    if (!message) return { __html: "" };

    // Use `marked` to parse Markdown
    const htmlContent = marked(message, {
      breaks: true,
      gfm: true,
      highlight: (code, lang) => {
        if (lang && hljs.getLanguage(lang)) {
          return hljs.highlight(code, { language: lang }).value;
        }
        return hljs.highlightAuto(code).value; // Auto-detect language
      },
    });

    return { __html: htmlContent };
  };


  const handleDragStart = (e) => {
    dragStartY.current = e.touches ? e.touches[0].clientY : e.clientY; // Support touch and mouse
  };

  const handleDragMove = (e) => {
    if (!dragStartY.current) return;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const dragDistance = dragStartY.current - currentY;

    // Close dropdown if dragged upward by a certain threshold
    if (dragDistance > 50) {
      setIsDropdownActive(false);
      dragStartY.current = null; // Reset drag state
    }
  };

  const handleDragEnd = () => {
    dragStartY.current = null; // Reset drag state
  };


  const handleModelToolkitToggle = () => {
    setIsModelToolkitDropdownActive((prev) => !prev); // Toggle Model/Toolkit dropdown
  };
  const handleHamburgerClick = (e) => {
    e.stopPropagation(); // Prevent triggering `handleOutsideClick`
    setIsDropdownActive((prev) => !prev); // Toggle the dropdown state
  };

  const handleOutsideClick = (e) => {
    // Close dropdown if the user clicks outside the dropdown area
    if (isDropdownActive && !e.target.closest(".dropdown")) {
      setIsDropdownActive(false);
    }
  };
  const handleTypeToggle = (newType) => {
    setType(newType);
    setSearchParams({ type: newType }); // Update query parameter
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim()) return;

    // Add the user's message to the chat history
    const newChatHistory = [
      ...chatHistory,
      { role: "user", content: currentMessage },
    ];
    setChatHistory(newChatHistory);

    // Add a placeholder for the assistant's loading animation
    setChatHistory((prev) => [
      ...prev,
      { role: "assistant", content: "..." }, // Initial loading animation
    ]);

    // Reset the message input and textarea height
    setCurrentMessage("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "60px";
    }

    // Prepare the data for the request
// Limit to the last 4 messages
const limitedChatHistory = newChatHistory.slice(-4);

// Check if the last message is a duplicate of the current user input
if (
  limitedChatHistory.length > 0 &&
  limitedChatHistory[limitedChatHistory.length - 1].content === currentMessage &&
  limitedChatHistory[limitedChatHistory.length - 1].role === "user"
) {
  // Remove the last message if it's a duplicate user message
  limitedChatHistory.pop();
}

const data = {
  user_input: currentMessage,
  model_name: selectedModel,
  chat_history: limitedChatHistory,
  ...(type === "agentic" && { tools: selectedTools }), // Include tools in agentic mode
};
    // Determine the API route based on the chat type
    const route = type === "agentic" ? "/agentic" : "/basic";

    try {
      // Make the API request
      const response = await fetch(
        `https://avachatbe.ai.aitech.io${route}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

      // Process the response
      if (type === "agentic") {
        const fullResponse = await response.text();
        setChatHistory((prev) => {
          const updatedHistory = [...prev];
          updatedHistory[updatedHistory.length - 1] = {
            role: "assistant",
            content: fullResponse,
          };
          return updatedHistory;
        });
      } else {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let done = false;
        let fullResponse = "";
        let buffer = "";

        while (!done) {
          const { done: readDone, value } = await reader.read();
          done = readDone;
          if (value) {
            buffer += decoder.decode(value, { stream: true });

            try {
              const jsonObjects = buffer.split("\n").filter(Boolean);
              for (const jsonObj of jsonObjects) {
                const jsonChunk = JSON.parse(jsonObj);
                const responseChunk = jsonChunk.chunk;
                fullResponse += responseChunk;

                setChatHistory((prev) => {
                  const updatedHistory = [...prev];
                  const lastMessage =
                    updatedHistory[updatedHistory.length - 1];
                  updatedHistory[updatedHistory.length - 1] = {
                    ...lastMessage,
                    content: fullResponse,
                  };
                  return updatedHistory;
                });
              }
              buffer = "";
            } catch {
              console.log(
                "Waiting for more chunks to form a complete JSON object"
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("Error during request:", error);

      // Update the assistant's message box with the error message
      setChatHistory((prev) => {
        const updatedHistory = [...prev];
        updatedHistory[updatedHistory.length - 1] = {
          role: "assistant",
          content: `An error occurred: ${error.message}`,
        };
        return updatedHistory;
      });
    }
  };



  return (
    <div className={`chat-mobile-container ${isIOS() ? "ios-device" : ""}`} onClick={handleOutsideClick}>
      {/* Navbar */}
      <TrendingCarousel/>
      {!isDropdownActive && (
        <nav className="chat-mobile-navbar">
          <div className="navbar-left">
            <img src={avatar} alt="Avatar" className="avatar" />
            <span className="boldText">AVA</span>
            <span className="normalText">CHAT</span>
          </div>
          <div className="navbar-right">
            <button className="navbar-button" onClick={() => window.open("https://pancakeswap.finance/?inputCurrency=0x55d398326f99059fF775485246999027B3197955&outputCurrency=0x2D060Ef4d6BF7f9e5edDe373Ab735513c0e4F944", "_blank", "noopener,noreferrer")}>BUY $AITECH</button>
            <button
              className="navbar-icon-button"
              onClick={() => window.open("https://x.com/Ava_AITECH", "_blank", "noopener,noreferrer")}
            >
              <img src={X} alt="Twitter" className="navbar-icon" />
            </button>

            <button
              className={`hamburger-icon ${isDropdownActive ? "active" : ""
                }`}
              onClick={handleHamburgerClick}
            >
              <span className="hamburger-dot"></span>
              <span className="hamburger-dot"></span>
              <span className="hamburger-dot"></span>
            </button>
          </div>
        </nav>
      )}



      {/* Dropdown Menu */}
      {isDropdownActive && (
        <div
          className="dropdown"
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          <div className="dropdown-avatar-container">
            <img src={avatar} alt="Avatar" className="dropdown-avatar" />
            <div className="dropdown-title">
              <span className="boldText">AVA</span>
              <span className="normalText">CHAT</span>
            </div>
          </div>

          {/* Toggle Between Basic and Agentic */}
          <div className="toggle-container">
            <div
              className={`toggle-option ${type === "basic" ? "active" : ""}`}
              onClick={() => handleTypeToggle("basic")}
            >
              Basic
            </div>
            <div
              className={`toggle-option ${type === "agentic" ? "active" : ""}`}
              onClick={() => handleTypeToggle("agentic")}
            >
              Agentic
            </div>
          </div>

          {/* Model/Toolkit Dropdown */}
          <div className="dropdown-container">
            <div className="dropdown-header" onClick={handleModelToolkitToggle}>
              {type === "basic" ? selectedModel : "Toolkit"}
              <span className="dropdown-arrow">
                {isModelToolkitDropdownActive ? <img src={dropup} alt="dropup" className="navbar-icon" /> : <img src={dropdown} alt="dropdown" className="navbar-icon" />}
              </span>
            </div>
            {isModelToolkitDropdownActive && (
              <div className="dropdown-options">
                {type === "basic" &&
                  modelOptions.map((model) => (
                    <div
                      key={model.name}
                      className={`dropdown-option ${selectedModel === model.name ? "selected" : ""
                        }`}
                      onClick={() => {
                        setSelectedModel(model.name); // Select the model
                        setIsModelToolkitDropdownActive(false); // Close the dropdown
                      }}
                    >
                      {model.name}
                    </div>
                  ))}

                {type === "agentic" &&
                  toolOptions.map((tool) => (
                    <div
                      key={tool}
                      className={`dropdown-option ${selectedTools.includes(tool) ? "selected" : ""
                        }`}
                      onClick={() =>
                        setSelectedTools((prev) =>
                          prev.includes(tool)
                            ? prev.filter((t) => t !== tool) // Remove if already selected
                            : [...prev, tool] // Add if not selected
                        )
                      }
                    >
                      {tool}
                      {selectedTools.includes(tool) && (
                        <div className="checkmark"> <img src={tick} alt="tick" className="navbar-icon" /></div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div className="dropdown-buttons">
            <button className="navbar-button" onClick={() => window.open("https://pancakeswap.finance/?inputCurrency=0x55d398326f99059fF775485246999027B3197955&outputCurrency=0x2D060Ef4d6BF7f9e5edDe373Ab735513c0e4F944", "_blank", "noopener,noreferrer")}>BUY $AITECH</button>
            <button className="navbar-icon-button" onClick={() => window.open("https://x.com/Ava_AITECH", "_blank", "noopener,noreferrer")}> FOLLOW ON
              <img src={X} alt="Twitter" className="navbar-icon" />
            </button>
          </div>
          <button
            className={`hamburger-icon-dropdown ${isDropdownActive ? "active" : ""}`}
            onClick={handleHamburgerClick}
          >
            <span className="cross-icon"><img src={exit} alt="exit" className="navbar-icon" /></span>
          </button>
        </div>
      )}
      <div
        className={`chat-mobile-history ${isDropdownActive ? "hide-history" : ""
          }`} ref={chatWindowRef}
      >
        {chatHistory.length === 0 ? (
          <p className="empty-history-message">Your chat history is empty. Start a conversation!</p>
        ) : (
          chatHistory.map((message, index) => (
            <div
              key={index}
              className={`chat-message ${
                message.role === "user" ? "user-message" : "assistant-message"
              }`}
              ref={index === chatHistory.length - 1 ? lastUserMessageRef : null} // Attach ref to the last message
            >
              {message.content === "..." ? (
                <div className="dot-loading">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              ) : message.role === "assistant" ? (
                <div
                  dangerouslySetInnerHTML={renderFormattedMessage(message.content)}
                />
              )
                : (
                  message.content
                )}
            </div>
          ))
        )}

      </div>



      <footer
  className={`chat-mobile-footer ${
    isTextareaFocused && isIOS() ? "textarea-focused" : ""
  }`}
>
  <button className="footer-icon-button">
    <img src={mic} alt="Mic" className="footer-icon" onClick={handleVoiceInput} />
  </button>
  <textarea
    placeholder={isRecording ? "Listening..." : "Ask me anything..."}
    className="chat-input"
    value={currentMessage}
    ref={textareaRef}
    onChange={(e) => setCurrentMessage(e.target.value)}
    onInput={(e) => {
      e.target.style.height = "auto";
      e.target.style.height = `${e.target.scrollHeight - 14}px`;
    }}
    onFocus={() => setIsTextareaFocused(true)}
    onBlur={() => setIsTextareaFocused(false)}
    disabled={isRecording}
  ></textarea>

  <button className="footer-icon-button" onClick={handleSendMessage}>
    <img src={send} alt="Send" className="footer-icon" />
  </button>
</footer>
<p className="disclaimer-desktop" style={{fontSize:"0.7rem", paddingLeft:"0.6rem", paddingRight:"0.6rem", color:"white", zIndex:10000}}>
All responses are AI generated. AI responses may be inaccurate. Use at your own discretion. See our 
  <a href="https://docs.ai.aitech.io/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color:"white", marginLeft: "4px", marginRight:"4px"}}>Terms of Service</a> 
  and 
  <a href="https://docs.ai.aitech.io/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color:"white", marginLeft: "4px", marginRight:"4px"}}>Privacy Policy.</a>
</p>

    </div>
  );
};

export default ChatMobile;
